import { useMemo, useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { DRAFT_COLUMN, MOBILE_APPS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { useAllDrafts, useMobileApps } from '@mobileHooks'
import { MobileApp } from '@mobileTypes'
import { Box, Button, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileApps = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllApps = matchPath(ROUTES.CONFIG_MOBILE_APPS, pathname)

  const { apps, loading, copyApp, deleteApp } = useMobileApps()
  const { entries: draftEntries, deleteDraft } = useAllDrafts<MobileApp>('mobile-app-draft-')

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [appToCopy, setAppToCopy] = useState<{ id: number; name: string; internalId: string } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [appToDelete, setAppToDelete] = useState<{ id: number; name: string } | null>(null)

  const handleCreate = () => navigate(ROUTES.CONFIG_MOBILE_APPS_CREATE)

  const handleEdit = (id: number) => navigate(`${ROUTES.CONFIG_MOBILE_APPS_EDIT}/${id}`)

  const handleCopyClick = (id: number) => {
    const app = apps.find(a => a.id === id)
    if (app) {
      setAppToCopy({ id: app.id, name: app.name, internalId: app.internalId })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = async (newName: string, newInternalId: string) => {
    if (appToCopy) {
      const newApp = await copyApp(appToCopy.id, newName, newInternalId)
      if (newApp) {
        setCopyDialogOpen(false)
        setAppToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_APPS_EDIT}/${newApp.id}`)
      }
    }
  }

  const handleDeleteClick = (id: number) => {
    const app = apps.find(a => a.id === id)
    if (app) {
      setAppToDelete({ id: app.id, name: app.name })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = async () => {
    if (appToDelete) {
      await deleteApp(appToDelete.id)
      setDeleteDialogOpen(false)
      setAppToDelete(null)
    }
  }

  const handleExport = (id: number) => {
    const app = apps.find(a => a.id === id)
    if (app) exportToJSON(app, `${app.name}_config.json`)
  }

  const rows = useMemo(() => {
    const draftEntityIdMap = new Map(
      draftEntries.filter(e => e.idPart !== 'new').map(e => [e.idPart, e]),
    )

    const newDraftRows = draftEntries
      .filter(e => e.idPart === 'new')
      .map(e => ({
        ...e.draft.data,
        id: 'draft-new',
        _isDraft: true,
        _isNewDraft: true,
        _draftKey: e.key,
      }))

    const entityDraftRows = apps
      .filter(a => draftEntityIdMap.has(String(a.id)))
      .map(a => {
        const entry = draftEntityIdMap.get(String(a.id))!
        return { ...a, ...entry.draft.data, id: a.id, _isDraft: true, _isNewDraft: false, _draftKey: entry.key }
      })

    const serverRows = apps.filter(a => !draftEntityIdMap.has(String(a.id)))

    return [...newDraftRows, ...entityDraftRows, ...serverRows]
  }, [apps, draftEntries])

  const columns = [
    DRAFT_COLUMN,
    ...MOBILE_APPS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: (params: any) => {
        if (params.row._isNewDraft) {
          navigate(ROUTES.CONFIG_MOBILE_APPS_CREATE, { state: { restoreDraft: true } })
        } else if (params.row._isDraft) {
          navigate(`${ROUTES.CONFIG_MOBILE_APPS_EDIT}/${params.row.id}`, { state: { restoreDraft: true } })
        } else {
          handleEdit(params.row.id)
        }
      },
      onCopy: (params: any) => {
        if (!params.row._isNewDraft) handleCopyClick(params.row.id)
      },
      onDelete: (params: any) => {
        if (params.row._isDraft) {
          deleteDraft(params.row._draftKey)
        } else {
          handleDeleteClick(params.row.id)
        }
      },
      onDowloadButtonClick: (params: any) => {
        if (!params.row._isNewDraft) handleExport(params.row.id)
      },
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy Mobile App'
        initialName={appToCopy?.name || ''}
        existingNames={apps.map(a => a.name)}
        initialInternalId={appToCopy?.internalId}
        existingInternalIds={apps.map(a => a.internalId)}
        onClose={() => { setCopyDialogOpen(false); setAppToCopy(null) }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete Mobile App'
        message='Are you sure you want to delete mobile app'
        itemName={appToDelete?.name}
        onClose={() => { setDeleteDialogOpen(false); setAppToDelete(null) }}
        onConfirm={handleDelete}
      />

      <Box sx={{ ...(!isAllApps && { display: 'none' }), flex: 1 }}>
        <PageContentLayout sx={{ p: 1 }}>
          <PageTitle
            end={false}
            start={
              <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                Mobile Apps
              </Typography>
            }
          />

          <Box sx={{ mb: 2 }}>
            <Button variant='contained' onClick={handleCreate}>
              Create Mobile App
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid rows={rows} columns={columns} loading={loading} hideFooter />
          </Box>
        </PageContentLayout>
      </Box>
    </>
  )
}
